import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Loader2, Check, Heart, ChevronLeft } from "lucide-react";

export default function FundraiserSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const eventId = params.get("event_id");
  const [status, setStatus] = useState("checking");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/donations/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts >= 8) {
          setStatus("pending");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attempts >= 8) setStatus("error");
        else setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId]);

  return (
    <AppShell>
      <div className="grid place-items-center py-10">
        <div className="bd-surface p-8 w-full max-w-lg text-center" data-testid="fundraiser-success">
          {status === "checking" && (
            <>
              <Loader2 className="animate-spin mx-auto mb-3" size={28} />
              <h2 className="text-2xl font-bold mb-2">Confirming your donation…</h2>
              <p style={{ color: "var(--text-dim)" }}>This usually takes a few seconds.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <span
                className="w-16 h-16 grid place-items-center mx-auto mb-3"
                style={{ background: "var(--primary)", color: "var(--primary-fg)", borderRadius: 9999 }}
              >
                <Check size={28} />
              </span>
              <h2 className="text-3xl font-extrabold mb-2" style={{ letterSpacing: "-0.04em" }}>Thank you!</h2>
              <p className="mb-3" style={{ color: "var(--text-dim)" }}>
                Your donation of <span className="bd-mono">${(info?.amount || 0).toFixed(2)}</span> has been credited.
              </p>
              <div className="flex justify-center gap-2 mt-4">
                {eventId && (
                  <Link to={`/events/${eventId}`} className="bd-btn" data-testid="back-to-event">
                    Back to event
                  </Link>
                )}
                <button className="bd-btn bd-btn-ghost" onClick={() => navigate("/app")} data-testid="back-to-calendar">
                  Calendar
                </button>
              </div>
            </>
          )}
          {status === "pending" && (
            <>
              <h2 className="text-2xl font-bold mb-2">Still processing.</h2>
              <p style={{ color: "var(--text-dim)" }}>You&apos;ll see it on the event page once it clears.</p>
            </>
          )}
          {status === "expired" && (
            <>
              <h2 className="text-2xl font-bold mb-2">Session expired.</h2>
              <p style={{ color: "var(--text-dim)" }}>No charge was made.</p>
            </>
          )}
          {status === "error" && (
            <>
              <h2 className="text-2xl font-bold mb-2">Something went wrong.</h2>
              <p style={{ color: "var(--text-dim)" }}>Please go back and try again.</p>
              {eventId && (
                <Link to={`/events/${eventId}`} className="bd-btn mt-4 inline-flex" data-testid="back-to-event-err">
                  <ChevronLeft size={14} /> Back to event
                </Link>
              )}
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}
